if (!customElements.get('wls-collection-filters')) {
  class WLSCollectionFilters extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        filterForm: 'wls-filters-form',
        productsGrid: 'wls-collection-grid',
        loading: 'is-loading',
        open: 'is-open',
        filterItem: 'wls-filters__filter-item',
        filterContent: '[id^="wls-filter-"]',
        filterIcon: 'wls-filter__filter-item-icon',
        priceRange: 'wls-filters__price-range',
        range: 'range',
        activeFilterRemove: 'wls-collection-grid__active-filter-remove',
        activeFilterTag: 'wls-collection-grid__active-filter-tag',
        paginationLink: '.wls-collection-grid__pagination a[href]',
        filterFooter: 'wls-filters__footer',
        filterLabels: 'wls-filters__filter-item-label-wrapper',
        sortSelect: 'wls-collection-grid__products-sort-select',
        mobileWrapper: 'wls-collection-grid__products-sort-title-inner',
        closeIcon: '.wls-collection-grid__filters-wrapper .wls-icon-close',
      };

      this.isApplyingFilters = false;
      
      // Bind methods
      this.boundFilterMobileToggle = this.filterMobileToggle.bind(this);
      this.boundCloseIconToggle = this.closeIconToggle.bind(this);
      this.boundHandleSortChange = this.handleSortChange.bind(this);
      this.boundHandleFormChange = this.handleFormChange.bind(this);
      this.boundHandleRangeInput = this.handleRangeInput.bind(this);
      this.boundHandlePriceInput = this.handlePriceInput.bind(this);
      this.boundHandlePopState = this.handlePopState.bind(this);
      this.boundHandleFormSubmit = this.handleFormSubmit.bind(this);
      this.boundHandleApplyClick = this.handleApplyClick.bind(this);
      this.boundHandleClearClick = this.handleClearClick.bind(this);
    }

    /**
     * @description Get selectors within this component
     */
    getSelectors() {
      const productsGrid = this.querySelector(`.${this.classes.productsGrid}`) || this.closest(`.${this.classes.productsGrid}`) || this;
      
      return {
        filterForm: productsGrid.querySelector(`#${this.classes.filterForm}`) || document.getElementById(this.classes.filterForm),
        clearFiltersBtn: productsGrid.querySelector('[data-action="clear-all-filters"]'),
        applyBtn: productsGrid.querySelector('[data-action="filtered-results"]'),
        filterLabels: productsGrid.querySelectorAll(`.${this.classes.filterLabels}`),
        sortSelect: productsGrid.querySelector(`.${this.classes.sortSelect}`),
        filterFooter: productsGrid.querySelector(`.${this.classes.filterFooter}`),
        productsGrid: productsGrid,
        mobileWrapper: productsGrid.querySelector(`.${this.classes.mobileWrapper}`),
        closeIcon: productsGrid.querySelector(this.classes.closeIcon),
      };
    }

    /**
     * @description Initialize component when connected to DOM
     */
    connectedCallback() {
      this.selectors = this.getSelectors();
      
      if (!this.selectors.productsGrid) {
        console.warn('WLSCollectionFilters: productsGrid not found');
        return;
      }

      this.filterMobileToggle();
      this.closeIconToggle();
      this.toggleFilterButtons();
      this.setupSortSync();
      this.setupFilterLabels();
      this.setupFilterForm();
      this.setupPriceRangeSliders();
      this.setupActiveFilterRemoval();
      this.setupPaginationLinks();
      this.setupApplyButton();
      this.setupClearButton();
      this.setupPopState();
    }

    /**
     * @description Cleanup when component is disconnected
     */
    disconnectedCallback() {
      // Remove event listeners
      if (this.selectors?.filterForm) {
        this.selectors.filterForm.removeEventListener('change', this.boundHandleFormChange);
        this.selectors.filterForm.removeEventListener('submit', this.boundHandleFormSubmit);
      }

      if (this.selectors?.mobileWrapper) {
        const handler = this.selectors.mobileWrapper.dataset.mobileToggleHandler;
        if (handler) {
          this.selectors.mobileWrapper.removeEventListener('click', handler);
        }
      }

      if (this.selectors?.closeIcon) {
        this.selectors.closeIcon.removeEventListener('click', this.boundCloseIconToggle);
      }

      window.removeEventListener('popstate', this.boundHandlePopState);
    }

    /**
     * @description Toggle mobile filter menu
     */
    filterMobileToggle() {
      if (!this.selectors.mobileWrapper) return;

      const existingHandler = this.selectors.mobileWrapper.dataset.mobileToggleHandler;
      if (existingHandler) {
        this.selectors.mobileWrapper.removeEventListener('click', existingHandler);
      }

      const clickHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.selectors.productsGrid.classList.toggle(this.classes.open);
      };

      this.selectors.mobileWrapper.dataset.mobileToggleHandler = clickHandler;
      this.selectors.mobileWrapper.addEventListener('click', clickHandler);
    }

    /**
     * @description Toggle close icon
     */
    closeIconToggle() {
      if (!this.selectors.closeIcon) return;
      this.selectors.closeIcon.addEventListener('click', (e) => {
        this.selectors.productsGrid.classList.remove(this.classes.open);
      });
    }

    /**
     * @description Check if any filters are selected
     */
    hasActiveFilters() {
      if (!this.selectors.filterForm) return false;

      // Check for checked checkboxes with filter names
      const checkedBoxes = this.selectors.filterForm.querySelectorAll('input[type="checkbox"]:checked');
      for (let cb of checkedBoxes) {
        if (cb.name && cb.name.includes('filter.')) {
          return true;
        }
      }

      // Check for price inputs with values
      const priceInputs = this.selectors.filterForm.querySelectorAll('input[type="number"][name*="filter.v.price"]');
      for (let input of priceInputs) {
        if (input.value && parseFloat(input.value) > 0) {
          return true;
        }
      }

      // Check for price range sliders that are not at default values
      const rangeInputs = this.selectors.filterForm.querySelectorAll(`.${this.classes.priceRange} .${this.classes.range}`);
      if (rangeInputs.length >= 2) {
        const minRange = parseFloat(rangeInputs[0].value) || 0;
        const maxRange = parseFloat(rangeInputs[1].value) || 0;
        const rangeMax = parseFloat(rangeInputs[0].max) || 0;

        if (minRange > 0 || maxRange < rangeMax) {
          return true;
        }
      }

      return false;
    }

    /**
     * @description Toggle filter buttons visibility
     */
    toggleFilterButtons() {
      if (!this.selectors.filterFooter) return;

      const hasActive = this.hasActiveFilters();
      this.selectors.filterFooter.style.display = hasActive ? '' : 'none';
      this.selectors.filterFooter.style.opacity = hasActive ? '1' : '0';
      this.selectors.filterFooter.style.visibility = hasActive ? 'visible' : 'hidden';
      this.selectors.filterFooter.style.pointerEvents = hasActive ? 'auto' : 'none';
    }

    /**
     * @description Setup pagination links
     */
    setupPaginationLinks() {
      const paginationLinks = this.selectors.productsGrid.querySelectorAll(this.classes.paginationLink);
      paginationLinks.forEach(link => {
        if (link.dataset.ajaxHandled) return;
        link.dataset.ajaxHandled = 'true';

        link.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          const href = link.getAttribute('href');
          if (!href || href === '#' || href === 'javascript:void(0);') {
            return;
          }

          const url = href.startsWith('http') ? href : window.location.origin + href;
          this.updateGridWithFilters(url);
        });
      });
    }

    /**
     * @description Setup active filter removal buttons
     */
    setupActiveFilterRemoval() {
      const activeFilterRemoveBtns = this.selectors.productsGrid.querySelectorAll(`.${this.classes.activeFilterRemove}`);
      activeFilterRemoveBtns.forEach(btn => {
        if (btn.dataset.ajaxHandled) return;
        btn.dataset.ajaxHandled = 'true';

        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          const filterTag = btn.closest(`.${this.classes.activeFilterTag}`);
          if (!filterTag) return;

          const filterName = filterTag.getAttribute('data-filter-name');
          const filterValue = filterTag.getAttribute('data-filter-value');

          if (!this.selectors.filterForm) return;

          // Handle price range filter removal
          if (filterName === 'price') {
            this.selectors.filterForm.querySelectorAll('input[name*="filter.v.price"]').forEach(input => {
              input.value = '';
            });

            const rangeInputs = this.selectors.filterForm.querySelectorAll(`.${this.classes.priceRange} .${this.classes.range}`);
            if (rangeInputs.length >= 2) {
              const rangeMax = rangeInputs[0].max;
              rangeInputs[0].value = 0;
              rangeInputs[1].value = rangeMax;
            }
          } else {
            const checkbox = this.selectors.filterForm.querySelector(`input[name="${CSS.escape(filterName)}"][value="${CSS.escape(filterValue)}"]`);
            if (checkbox) {
              checkbox.checked = false;
            }
          }

          // Build new URL without this filter
          const currentUrl = new URL(window.location.href);
          const params = new URLSearchParams(currentUrl.search);

          if (filterName === 'price') {
            params.delete('filter.v.price.gte');
            params.delete('filter.v.price.lte');
          } else {
            const values = params.getAll(filterName);
            params.delete(filterName);
            values.forEach(val => {
              if (val !== filterValue) {
                params.append(filterName, val);
              }
            });
          }

          const newUrl = currentUrl.pathname + (params.toString() ? '?' + params.toString() : '');
          const absoluteUrl = newUrl.startsWith('http') ? newUrl : window.location.origin + newUrl;

          this.updateGridWithFilters(absoluteUrl);
        });
      });
    }

    /**
     * @description Update grid via AJAX
     */
    updateGridWithFilters(url) {
      if (this.isApplyingFilters || !this.selectors.productsGrid) return;

      this.isApplyingFilters = true;
      this.selectors.productsGrid.classList.add(this.classes.loading);

      // Ensure URL is absolute and preserve view parameter
      let absoluteUrl = url;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        if (url.startsWith('/')) {
          absoluteUrl = window.location.origin + url;
        } else {
          absoluteUrl = window.location.origin + '/' + url;
        }
      }

      // Preserve view parameter if it exists in current URL
      const currentUrl = new URL(window.location.href);
      const currentView = currentUrl.searchParams.get('view');
      if (currentView) {
        const urlObj = new URL(absoluteUrl);
        if (!urlObj.searchParams.has('view')) {
          urlObj.searchParams.set('view', currentView);
          absoluteUrl = urlObj.toString();
        }
      }

      fetch(absoluteUrl, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'text/html'
        }
      })
        .then(res => {
          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
          }
          return res.text();
        })
        .then(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const newGrid = doc.querySelector(`.${this.classes.productsGrid}`);

          if (newGrid) {
            // Update the products sort section
            const currentProductsSort = this.selectors.productsGrid.querySelector('.wls-collection-grid__products-sort');
            const newProductsSort = newGrid.querySelector('.wls-collection-grid__products-sort');
            if (currentProductsSort && newProductsSort) {
              const currentSortValue = currentProductsSort.querySelector(`.${this.classes.sortSelect}`)?.value;
              currentProductsSort.innerHTML = newProductsSort.innerHTML;
              const newSortSelect = currentProductsSort.querySelector(`.${this.classes.sortSelect}`);
              if (newSortSelect && currentSortValue) {
                newSortSelect.value = currentSortValue;
              }
            }

            // Update mobile active filters section
            const currentActiveFiltersMob = this.selectors.productsGrid.querySelector('.wls-collection-grid__active-filters-outer-mob');
            const newActiveFiltersMob = newGrid.querySelector('.wls-collection-grid__active-filters-outer-mob');
            if (currentActiveFiltersMob && newActiveFiltersMob) {
              currentActiveFiltersMob.innerHTML = newActiveFiltersMob.innerHTML;
            }

            // Update only the products list wrapper content
            const currentWrapper = this.selectors.productsGrid.querySelector('.wls-collection-grid__wrapper');
            const newWrapper = newGrid.querySelector('.wls-collection-grid__wrapper');

            if (currentWrapper && newWrapper) {
              const currentFiltersWrapper = currentWrapper.querySelector('.wls-collection-grid__filters-wrapper');
              const newProductsListWrapper = newWrapper.querySelector('.wls-collection-grid__products-list-wrapper');
              const newEmptyState = newWrapper.querySelector('.wls-collection-grid__wrapper-empty-state');
              const currentProductsListWrapper = currentWrapper.querySelector('.wls-collection-grid__products-list-wrapper');
              const currentEmptyState = currentWrapper.querySelector('.wls-collection-grid__wrapper-empty-state');

              if (currentProductsListWrapper) {
                currentProductsListWrapper.remove();
              }
              if (currentEmptyState) {
                currentEmptyState.remove();
              }

              if (newProductsListWrapper) {
                const clonedProductsList = newProductsListWrapper.cloneNode(true);
                clonedProductsList.querySelectorAll('wls-variant-selector').forEach((el) => {
                  customElements.upgrade(el);
                });
                if (currentFiltersWrapper) {
                  currentFiltersWrapper.insertAdjacentElement('afterend', clonedProductsList);
                } else {
                  currentWrapper.appendChild(clonedProductsList);
                }
              } else if (newEmptyState) {
                const clonedEmptyState = newEmptyState.cloneNode(true);
                if (currentFiltersWrapper) {
                  currentFiltersWrapper.insertAdjacentElement('afterend', clonedEmptyState);
                } else {
                  currentWrapper.appendChild(clonedEmptyState);
                }
              }
            }

            // Update URL without page reload
            window.history.pushState({}, '', absoluteUrl);

            // Re-initialize after DOM update
            setTimeout(() => {
              this.selectors = this.getSelectors();
              this.setupSortSync();
              this.setupActiveFilterRemoval();
              this.setupPaginationLinks();
              this.filterMobileToggle();
              this.toggleFilterButtons();
              this.isApplyingFilters = false;
            }, 100);
          } else {
            window.location.href = absoluteUrl;
          }
        })
        .catch(err => {
          console.error('Error updating grid:', err);
          window.location.href = absoluteUrl;
        })
        .finally(() => {
          this.selectors.productsGrid.classList.remove(this.classes.loading);
          this.selectors.productsGrid.classList.remove(this.classes.open);
        });
    }

    /**
     * @description Setup sort sync
     */
    setupSortSync() {
      const currentSortSelect = this.selectors.productsGrid.querySelector(`.${this.classes.sortSelect}`);
      if (!currentSortSelect) return;

      const sortInput = this.selectors.filterForm?.querySelector('input[name="sort_by"]');

      const urlParams = new URLSearchParams(window.location.search);
      const urlSortBy = urlParams.get('sort_by');

      let sortValue = urlSortBy || sortInput?.value || 'featured';

      const optionExists = Array.from(currentSortSelect.options).some(opt => opt.value === sortValue);
      if (!optionExists) {
        sortValue = 'featured';
      }

      currentSortSelect.value = sortValue;
      if (sortInput) {
        sortInput.value = sortValue;
      }

      currentSortSelect.removeEventListener('change', this.boundHandleSortChange);
      currentSortSelect.addEventListener('change', this.boundHandleSortChange);
    }

    /**
     * @description Handle sort change
     */
    handleSortChange(e) {
      const selectedSort = e.target.value;
      const sortInput = this.selectors.filterForm?.querySelector('input[name="sort_by"]');

      if (sortInput) {
        sortInput.value = selectedSort;
      }

      const currentUrl = new URL(window.location.href);
      const params = new URLSearchParams(currentUrl.search);
      
      // Only set sort_by if we have a valid value
      if (selectedSort) {
        params.set('sort_by', selectedSort);
      } else {
        params.delete('sort_by');
      }

      const newUrl = currentUrl.pathname + (params.toString() ? '?' + params.toString() : '');
      const absoluteUrl = newUrl.startsWith('http') ? newUrl : window.location.origin + newUrl;

      this.updateGridWithFilters(absoluteUrl);
    }

    /**
     * @description Setup filter labels toggle
     */
    setupFilterLabels() {
      this.selectors.filterLabels.forEach((label) => {
        label.addEventListener('click', () => {
          const filterItem = label.closest(`.${this.classes.filterItem}`);
          const filterContent = filterItem?.querySelector(this.classes.filterContent);
          if (!filterContent || !filterContent.classList.contains('wls-filters__accordion-panel')) {
            return;
          }
          const filterList = filterItem.closest('.wls-filters__filter-list');
          const wasOpen = filterContent.classList.contains('is-open');
          filterList?.querySelectorAll('.wls-filters__accordion-panel').forEach((panel) => {
            panel.classList.remove('is-open');
          });
          filterList?.querySelectorAll(`.${this.classes.filterLabels}`).forEach((trigger) => {
            trigger.setAttribute('aria-expanded', 'false');
            const ic = trigger.querySelector(`.${this.classes.filterIcon}`);
            if (ic) ic.classList.remove('is-rotated');
          });
          if (!wasOpen) {
            filterContent.classList.add('is-open');
            label.setAttribute('aria-expanded', 'true');
            const icon = label.querySelector(`.${this.classes.filterIcon}`);
            if (icon) icon.classList.add('is-rotated');
          }
        });
      });
    }

    /**
     * @description Setup filter form listeners
     */
    setupFilterForm() {
      if (!this.selectors.filterForm) return;

      this.selectors.filterForm.addEventListener('change', this.boundHandleFormChange);
      this.selectors.filterForm.addEventListener('submit', this.boundHandleFormSubmit);
    }

    /**
     * @description Handle form change
     */
    handleFormChange(e) {
      if (e.target.type === 'checkbox' || e.target.type === 'number') {
        this.toggleFilterButtons();
      }
    }

    /**
     * @description Handle form submit
     */
    handleFormSubmit(e) {
      e.preventDefault();
      e.stopPropagation();
      if (this.selectors.applyBtn && !this.isApplyingFilters) {
        this.selectors.applyBtn.click();
      }
      return false;
    }

    /**
     * @description Setup price range sliders
     */
    setupPriceRangeSliders() {
      if (!this.selectors.filterForm) return;

      const rangeInputs = this.selectors.filterForm.querySelectorAll(`.${this.classes.priceRange} .${this.classes.range}`);
      rangeInputs.forEach((range, index) => {
        range.addEventListener('input', this.boundHandleRangeInput);
      });

      const priceInputs = this.selectors.filterForm.querySelectorAll('input[type="number"][name*="filter.v.price"]');
      priceInputs.forEach(input => {
        input.addEventListener('input', this.boundHandlePriceInput);
      });
    }

    /**
     * @description Handle range input
     */
    handleRangeInput(e) {
      const range = e.target;
      const rangeInputs = this.selectors.filterForm.querySelectorAll(`.${this.classes.priceRange} .${this.classes.range}`);
      const minRange = rangeInputs[0];
      const maxRange = rangeInputs[1];
      const priceRangeContainer = range.closest(`.${this.classes.priceRange}`);
      const minInput = priceRangeContainer?.querySelector('input[name*="filter.v.price.gte"]');
      const maxInput = priceRangeContainer?.querySelector('input[name*="filter.v.price.lte"]');

      const index = Array.from(rangeInputs).indexOf(range);

      if (index === 0 && minRange.value > maxRange.value) {
        minRange.value = maxRange.value;
      }
      if (index === 1 && maxRange.value < minRange.value) {
        maxRange.value = minRange.value;
      }

      if (minInput) minInput.value = Math.ceil(minRange.value);
      if (maxInput) maxInput.value = Math.ceil(maxRange.value);

      this.toggleFilterButtons();
    }

    /**
     * @description Handle price input
     */
    handlePriceInput() {
      this.toggleFilterButtons();
    }

    /**
     * @description Setup apply button
     */
    setupApplyButton() {
      if (!this.selectors.applyBtn) return;

      this.selectors.applyBtn.addEventListener('click', this.boundHandleApplyClick);
    }

    /**
     * @description Handle apply button click
     */
    handleApplyClick(e) {
      e.preventDefault();
      e.stopPropagation();

      if (this.selectors.productsGrid) {
        this.selectors.productsGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      if (this.isApplyingFilters) return;

      if (this.selectors.filterForm) {
        // Get sort value from select dropdown, fallback to form input, then to 'featured'
        let sortBy = null;
        if (this.selectors.sortSelect && this.selectors.sortSelect.value) {
          sortBy = this.selectors.sortSelect.value;
        } else {
          const sortInput = this.selectors.filterForm.querySelector('input[name="sort_by"]');
          sortBy = sortInput?.value || 'featured';
        }
        
        // Update form input if it exists
        if (this.selectors.filterForm.querySelector('input[name="sort_by"]')) {
          this.selectors.filterForm.querySelector('input[name="sort_by"]').value = sortBy;
        }

        const formData = new FormData(this.selectors.filterForm);
        const params = new URLSearchParams();

        const currentUrl = new URL(window.location.href);
        const currentView = currentUrl.searchParams.get('view');
        if (currentView) {
          params.append('view', currentView);
        }

        // Only add sort_by if we have a valid value
        if (sortBy) {
          params.append('sort_by', sortBy);
        }

        const searchQuery = formData.get('q');
        if (searchQuery) {
          params.append('q', searchQuery);
        }

        const processedKeys = new Set();
        formData.forEach((value, key) => {
          if (processedKeys.has(key) || key === 'sort_by' || key === 'q') {
            return;
          }

          processedKeys.add(key);
          const allValues = formData.getAll(key);

          allValues.forEach(val => {
            if (val && val !== '') {
              params.append(key, val);
            }
          });
        });

        const baseUrl = this.selectors.filterForm.action || window.location.pathname;
        const url = baseUrl + (params.toString() ? '?' + params.toString() : '');
        const absoluteUrl = url.startsWith('http') ? url : window.location.origin + url;

        this.updateGridWithFilters(absoluteUrl);
      }

      return false;
    }

    /**
     * @description Setup clear button
     */
    setupClearButton() {
      if (!this.selectors.clearFiltersBtn) return;

      this.selectors.clearFiltersBtn.addEventListener('click', this.boundHandleClearClick);
    }

    /**
     * @description Handle clear button click
     */
    handleClearClick(e) {
      e.preventDefault();
      e.stopPropagation();

      if (this.isApplyingFilters) return;

      if (this.selectors.filterForm) {
        this.selectors.filterForm.querySelectorAll('input[type="checkbox"]').forEach(cb => {
          cb.checked = false;
        });

        this.selectors.filterForm.querySelectorAll('input[type="number"]').forEach(input => {
          input.value = '';
        });

        const rangeInputs = this.selectors.filterForm.querySelectorAll(`.${this.classes.priceRange} .${this.classes.range}`);
        if (rangeInputs.length >= 2) {
          const rangeMax = rangeInputs[0].max;
          rangeInputs[0].value = 0;
          rangeInputs[1].value = rangeMax;
        }

        this.toggleFilterButtons();

        const baseUrl = this.selectors.filterForm.action || window.location.pathname;
        const currentUrl = new URL(window.location.href);
        const currentView = currentUrl.searchParams.get('view');
        
        // Get sort value from select dropdown, fallback to form input, then to 'featured'
        let sortBy = 'featured';
        if (this.selectors.sortSelect && this.selectors.sortSelect.value) {
          sortBy = this.selectors.sortSelect.value;
        } else {
          const sortInput = this.selectors.filterForm.querySelector('input[name="sort_by"]');
          sortBy = sortInput?.value || 'featured';
        }
        
        const searchQuery = this.selectors.filterForm.querySelector('input[name="q"]')?.value;

        let newUrl = baseUrl;
        const params = new URLSearchParams();

        if (currentView) {
          params.append('view', currentView);
        }

        // Only add sort_by if we have a valid value
        if (sortBy) {
          params.append('sort_by', sortBy);
        }
        
        if (searchQuery) {
          params.append('q', searchQuery);
        }

        if (params.toString()) {
          newUrl += '?' + params.toString();
        }

        const absoluteUrl = newUrl.startsWith('http') ? newUrl : window.location.origin + newUrl;

        this.updateGridWithFilters(absoluteUrl);
      }

      return false;
    }

    /**
     * @description Setup popstate handler
     */
    setupPopState() {
      window.addEventListener('popstate', this.boundHandlePopState);
    }

    /**
     * @description Handle browser back/forward buttons
     */
    handlePopState() {
      this.updateGridWithFilters(window.location.href);
    }
  }

  customElements.define('wls-collection-filters', WLSCollectionFilters);
}